import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ROLE_COLORS, ROLE_LABELS, formatPhoneNumber } from '../utils/teamMemberDisplay';

interface PendingHire {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
  completed_at: string | null;
  created_at: string;
  record?: {
    legal_name: string;
    phone: string;
    contract_signed: boolean;
    sms_consent: boolean;
    work_email_requested: string;
    shoe_size: string;
    shirt_size: string;
  };
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  phone: string;
  active: boolean;
  created_at: string;
  sms_consent: boolean;
  sms_consent_at: string | null;
}

function Badge({ label, color }: { label: string; color: string }) {
  return <span style={{ background: color + '22', color, border: `1px solid ${color}44`, borderRadius: 999, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{label}</span>;
}

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'salesman' });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteSent, setInviteSent] = useState('');
  const [editUser, setEditUser] = useState<User | null>(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'salesman', phone: '', sms_consent: false });
  const [editForm, setEditForm] = useState({ name: '', email: '', phone: '', role: '', password: '', sms_consent: false });
  const [saving, setSaving] = useState(false);
  const [pendingHires, setPendingHires] = useState<PendingHire[]>([]);
  const [activating, setActivating] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [removingUser, setRemovingUser] = useState<string | null>(null);

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data)).finally(() => setLoading(false));
    fetchPendingHires();
  }, []);

  const fetchPendingHires = async () => {
    try {
      const { data } = await api.get('/onboarding/invites');
      const completed = (data || []).filter((i: PendingHire) => i.status === 'complete');
      // Get employee records for each
      const withRecords = await Promise.all(completed.map(async (invite: PendingHire) => {
        try {
          const r = await api.get(`/onboarding/records-by-invite/${invite.id}`);
          return { ...invite, record: r.data };
        } catch { return invite; }
      }));
      setPendingHires(withRecords);
    } catch (e) { console.error(e); }
  };

  const activateHire = async (invite: PendingHire) => {
    if (!window.confirm(`Activate ${invite.name} and create their CRM account?\n\nThis will send them a welcome email with login credentials.`)) return;
    setActivating(invite.id);
    try {
      await api.post('/onboarding/activate', {
        invite_id: invite.id,
        name: invite.record?.legal_name || invite.name,
        email: invite.record?.work_email_requested || invite.email,
        personal_email: invite.email,
        role: invite.role,
        phone: invite.record?.phone || '',
        sms_consent: invite.record?.sms_consent || false,
      });
      const [{ data }] = await Promise.all([
        api.get('/users'),
        fetchPendingHires(),
      ]);
      setUsers(data);
      alert(`✅ ${invite.name} activated! A welcome email with login credentials has been sent to ${invite.email}.`);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to activate');
    } finally { setActivating(null); }
  };

  const deletePendingHire = async (invite: PendingHire) => {
    if (!window.confirm(`Permanently delete ${invite.record?.legal_name || invite.name} from Pending Activation?\n\nThis removes their completed onboarding profile and uploaded onboarding documents. This cannot be undone.`)) return;
    setDeleting(invite.id);
    try {
      const { data } = await api.delete(`/onboarding/invites/${invite.id}`);
      await fetchPendingHires();
      if (data.cleanup_warning) {
        alert(`${invite.name} was removed, but one or more uploaded files could not be cleaned up. Please contact an administrator.`);
      }
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to delete pending hire');
    } finally { setDeleting(null); }
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setInviteSaving(true);
    try {
      await api.post('/onboarding/invite', inviteForm);
      setInviteSent(inviteForm.email);
      setInviteForm({ name: '', email: '', role: 'salesman' });
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to send invite');
    } finally { setInviteSaving(false); }
  };

  const addUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload: any = { ...form };
      if (form.sms_consent) payload.sms_consent_at = new Date().toISOString();
      await api.post('/users', payload);
      const { data } = await api.get('/users');
      setUsers(data);
      setShowAdd(false);
      setForm({ name: '', email: '', password: '', role: 'salesman', phone: '', sms_consent: false });
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to create user');
    } finally { setSaving(false); }
  };

  const removeFromTeam = async (member: User) => {
    if (!window.confirm(`Delete ${member.name} from the active Team roster?\n\nTheir CRM access will be disabled. Their information will remain in Employees with an Inactive status, where access can be restored.`)) return;
    setRemovingUser(member.id);
    try {
      await api.delete(`/users/${member.id}`);
      setUsers(prev => prev.filter(u => u.id !== member.id));
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to remove team member');
    } finally { setRemovingUser(null); }
  };

  const openEdit = (user: User) => {
    setEditUser(user);
    setEditForm({ name: user.name, email: user.email, phone: user.phone || '', role: user.role, password: '', sms_consent: user.sms_consent || false });
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editUser) return;
    setSaving(true);
    try {
      const updates: any = { name: editForm.name, email: editForm.email, phone: editForm.phone, role: editForm.role, sms_consent: editForm.sms_consent };
      if (editForm.password) updates.password = editForm.password;
      // Log consent timestamp when toggled on
      if (editForm.sms_consent && !editUser.sms_consent) updates.sms_consent_at = new Date().toISOString();
      if (!editForm.sms_consent) updates.sms_consent_at = null;
      await api.patch(`/users/${editUser.id}`, updates);
      setUsers(prev => prev.map(u => u.id === editUser.id ? { ...u, ...updates } : u));
      setEditUser(null);
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to update user');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ padding: 32 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>Team</h1>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowInvite(!showInvite)}
            style={{ background: '#10B981', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700 }}>
            ✉️ Invite New Hire
          </button>
          <button onClick={() => setShowAdd(!showAdd)}
            style={{ background: '#4A7EB5', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700 }}>
            + Add Member
          </button>
        </div>
      </div>

      {/* Invite New Hire */}
      {showInvite && (
        <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, marginBottom: 24, border: '1px solid #10B98133' }}>
          <h3 style={{ color: '#10B981', marginBottom: 8 }}>✉️ Invite New Hire</h3>
          <p style={{ color: '#8FA8C0', fontSize: 13, marginBottom: 16 }}>Send an onboarding link to a new employee's personal email. They'll complete their contract, payroll setup, and training schedule online.</p>
          {inviteSent ? (
            <div style={{ background: '#10B98122', border: '1px solid #10B981', borderRadius: 10, padding: 16, color: '#10B981' }}>
              ✅ Invite sent to <strong>{inviteSent}</strong>. They'll receive the onboarding link shortly.
              <button onClick={() => { setInviteSent(''); setShowInvite(false); }} style={{ display: 'block', marginTop: 10, background: 'none', border: 'none', color: '#10B981', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}>Send another invite</button>
            </div>
          ) : (
            <form onSubmit={sendInvite} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#8FA8C0', marginBottom: 5, textTransform: 'uppercase' }}>Full Name</label>
                <input value={inviteForm.name} onChange={e => setInviteForm({...inviteForm, name: e.target.value})} required placeholder="John Smith" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#243447', border: '1px solid #2D4259', color: '#fff', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#8FA8C0', marginBottom: 5, textTransform: 'uppercase' }}>Personal Email</label>
                <input type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} required placeholder="john@gmail.com" style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#243447', border: '1px solid #2D4259', color: '#fff', fontSize: 14 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#8FA8C0', marginBottom: 5, textTransform: 'uppercase' }}>Role</label>
                <select value={inviteForm.role} onChange={e => setInviteForm({...inviteForm, role: e.target.value})} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, background: '#243447', border: '1px solid #2D4259', color: '#fff', fontSize: 14 }}>
                  <option value="salesman">Salesman</option>
                  <option value="estimator">Estimator</option>
                  <option value="foreman">Foreman</option>
                  <option value="contracting_officer">Contracting Officer</option>
                </select>
              </div>
              <button type="submit" disabled={inviteSaving} style={{ padding: '10px 20px', background: '#10B981', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {inviteSaving ? 'Sending...' : 'Send Invite'}
              </button>
            </form>
          )}
        </div>
      )}

      {/* Add User Form */}
      {showAdd && (
        <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 24, marginBottom: 24, border: '1px solid #2D4259' }}>
          <h3 style={{ color: '#fff', marginBottom: 20 }}>New Team Member</h3>
          <form onSubmit={addUser} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {[
              { label: 'Full Name', key: 'name', type: 'text', placeholder: 'John Smith' },
              { label: 'Email', key: 'email', type: 'email', placeholder: 'john@mcmullenbrowne.com' },
              { label: 'Password', key: 'password', type: 'password', placeholder: 'Temporary password' },
              { label: 'Phone', key: 'phone', type: 'tel', placeholder: '(555) 555-5555' },
            ].map(field => (
              <div key={field.key}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#8FA8C0', marginBottom: 6, textTransform: 'uppercase' }}>{field.label}</label>
                <input
                  type={field.type}
                  placeholder={field.placeholder}
                  value={(form as any)[field.key]}
                  onChange={e => setForm({ ...form, [field.key]: e.target.value })}
                  required={field.key !== 'phone'}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 10, background: '#243447', border: '1px solid #2D4259', color: '#fff', fontSize: 14 }}
                />
              </div>
            ))}
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#8FA8C0', marginBottom: 6, textTransform: 'uppercase' }}>Role</label>
              <select
                value={form.role}
                onChange={e => setForm({ ...form, role: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 10, background: '#243447', border: '1px solid #2D4259', color: '#fff', fontSize: 14 }}
              >
                <option value="salesman">Salesman</option>
                <option value="estimator">Estimator</option>
                <option value="foreman">Foreman</option>
                <option value="contracting_officer">Contracting Officer</option>
                <option value="specialist">Roof Specialist</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {/* SMS Consent — full width */}
            <div style={{ gridColumn: '1 / -1', background: '#0F1923', borderRadius: 10, border: '1px solid #2D4259', padding: '14px 16px' }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={form.sms_consent}
                  onChange={e => setForm({ ...form, sms_consent: e.target.checked })}
                  style={{ marginTop: 3, width: 16, height: 16, accentColor: '#4A7EB5', flexShrink: 0 }}
                />
                <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.5 }}>
                  <strong style={{ color: '#fff' }}>Employee SMS Consent</strong> — This employee has provided their mobile number and consents to receive internal SMS notifications from McMullen Browne Development LLC. Message &amp; data rates may apply. They may reply STOP at any time to opt out.
                </span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
              <button type="submit" disabled={saving} style={{ flex: 1, padding: '10px 0', background: '#4A7EB5', color: '#fff', borderRadius: 10, fontWeight: 700, fontSize: 14 }}>
                {saving ? 'Creating...' : 'Create Account'}
              </button>
              <button type="button" onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '10px 0', background: '#243447', color: '#8FA8C0', borderRadius: 10, fontSize: 14, border: '1px solid #2D4259' }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Modal */}
      {editUser && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#1A2B3C', borderRadius: 16, padding: 28, width: 480, border: '1px solid #2D4259' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ color: '#fff', margin: 0 }}>Edit — {editUser.name}</h3>
              <button onClick={() => setEditUser(null)} style={{ background: 'none', border: 'none', color: '#8FA8C0', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            <form onSubmit={saveEdit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              {[
                { label: 'Full Name', key: 'name', type: 'text' },
                { label: 'Email', key: 'email', type: 'email' },
                { label: 'Phone', key: 'phone', type: 'tel' },
                { label: 'New Password', key: 'password', type: 'password', placeholder: 'Leave blank to keep current' },
              ].map(field => (
                <div key={field.key}>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#8FA8C0', marginBottom: 5, textTransform: 'uppercase' }}>{field.label}</label>
                  <input
                    type={field.type}
                    placeholder={field.placeholder || ''}
                    value={(editForm as any)[field.key]}
                    onChange={e => setEditForm({ ...editForm, [field.key]: e.target.value })}
                    style={{ width: '100%', padding: '9px 12px', borderRadius: 8, background: '#243447', border: '1px solid #2D4259', color: '#fff', fontSize: 14, boxSizing: 'border-box' }}
                  />
                </div>
              ))}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#8FA8C0', marginBottom: 5, textTransform: 'uppercase' }}>Role</label>
                <select value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}
                  style={{ width: '100%', padding: '9px 12px', borderRadius: 8, background: '#243447', border: '1px solid #2D4259', color: '#fff', fontSize: 14 }}>
                  <option value="salesman">Salesman</option>
                  <option value="estimator">Estimator</option>
                  <option value="foreman">Foreman</option>
                  <option value="pm">Production Manager</option>
                  <option value="specialist">Roof Specialist</option>
                  <option value="contracting_officer">Contracting Officer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {/* SMS Consent */}
              <div style={{ gridColumn: '1 / -1', background: '#0F1923', borderRadius: 10, border: '1px solid #2D4259', padding: '14px 16px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editForm.sms_consent}
                    onChange={e => setEditForm({ ...editForm, sms_consent: e.target.checked })}
                    style={{ marginTop: 3, width: 16, height: 16, accentColor: '#4A7EB5', flexShrink: 0 }}
                  />
                  <span style={{ fontSize: 13, color: '#cbd5e1', lineHeight: 1.5 }}>
                    <strong style={{ color: '#fff' }}>Employee SMS Consent</strong> — This employee consents to receive internal SMS notifications from McMullen Browne Development LLC. Message &amp; data rates may apply. Reply STOP to opt out.
                    {editUser?.sms_consent_at && (
                      <span style={{ display: 'block', color: '#10B981', fontSize: 11, marginTop: 4 }}>
                        ✅ Consented {new Date(editUser.sms_consent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                    )}
                  </span>
                </label>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                <button type="submit" disabled={saving} style={{ flex: 1, padding: '10px 0', background: '#4A7EB5', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button type="button" onClick={() => setEditUser(null)} style={{ flex: 1, padding: '10px 0', background: '#243447', color: '#8FA8C0', borderRadius: 8, fontSize: 14, border: '1px solid #2D4259', cursor: 'pointer' }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pending Onboarding */}
      {pendingHires.length > 0 && (
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ color: '#F59E0B', fontSize: 18, fontWeight: 800, marginBottom: 12 }}>⏳ Pending Activation ({pendingHires.length})</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pendingHires.map(hire => (
              <div key={hire.id} style={{ background: '#1A2B3C', border: '1px solid #F59E0B44', borderRadius: 12, padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <div style={{ color: '#fff', fontWeight: 800, fontSize: 16 }}>{hire.record?.legal_name || hire.name}</div>
                  <div style={{ color: '#8FA8C0', fontSize: 13, marginTop: 2 }}>{hire.email} · <span>{ROLE_LABELS[hire.role] || hire.role}</span></div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    <Badge label={`Contract: ${hire.record?.contract_signed ? '✅ Signed' : '❌ Not signed'}`} color={hire.record?.contract_signed ? '#10B981' : '#EF4444'} />
                    <Badge label={`SMS: ${hire.record?.sms_consent ? '✅ Consented' : '❌ No consent'}`} color={hire.record?.sms_consent ? '#10B981' : '#EF4444'} />
                    {hire.record?.work_email_requested && <Badge label={`Email: ${hire.record.work_email_requested}`} color='#4A7EB5' />}
                    {hire.completed_at && <Badge label={`Completed ${new Date(hire.completed_at).toLocaleDateString()}`} color='#8B5CF6' />}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => activateHire(hire)}
                    disabled={activating === hire.id || deleting === hire.id}
                    style={{ background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: activating === hire.id || deleting === hire.id ? 0.6 : 1 }}
                  >
                    {activating === hire.id ? 'Activating...' : '✅ Activate & Create Account'}
                  </button>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => deletePendingHire(hire)}
                      disabled={activating === hire.id || deleting === hire.id}
                      style={{ background: '#7F1D1D', color: '#FCA5A5', border: '1px solid #EF444466', borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer', opacity: activating === hire.id || deleting === hire.id ? 0.6 : 1 }}
                    >
                      {deleting === hire.id ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Team Table */}
      <div style={{ background: '#1A2B3C', borderRadius: 16, border: '1px solid #2D4259', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 1000, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #2D4259' }}>
              {['Name', 'Email', 'Phone', 'Role', 'Status', 'Joined', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && users.every(u => !u.active) && (
              <tr><td colSpan={7} style={{ padding: 32, textAlign: 'center', color: '#8FA8C0' }}>No active team members</td></tr>
            )}
            {users.filter(u => u.active).map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid #2D4259' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#243447')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <td style={{ padding: '14px 16px', fontWeight: 600, color: '#fff' }}>{u.name}</td>
                <td style={{ padding: '14px 16px', color: '#4A7EB5', fontSize: 13 }}>{u.email}</td>
                <td style={{ padding: '14px 16px', color: '#8FA8C0', fontSize: 13, whiteSpace: 'nowrap' }}>{formatPhoneNumber(u.phone)}</td>
                <td style={{ padding: '14px 16px', minWidth: 150 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', background: (ROLE_COLORS[u.role] || '#4A7EB5') + '22', color: ROLE_COLORS[u.role] || '#4A7EB5', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' }}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ color: u.active ? '#10B981' : '#EF4444', fontSize: 13, fontWeight: 600 }}>
                    {u.active ? '● Active' : '○ Inactive'}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', color: '#8FA8C0', fontSize: 13 }}>{new Date(u.created_at).toLocaleDateString()}</td>
                <td style={{ padding: '14px 16px', minWidth: 236 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => openEdit(u)}
                      style={{ flex: '0 0 84px', height: 36, padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: '#4A7EB522', color: '#4A7EB5', border: '1px solid #4A7EB544', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      ✏️ Edit
                    </button>
                    {user?.role === 'admin' && u.id !== user.id && (
                      <button
                        onClick={() => removeFromTeam(u)}
                        disabled={removingUser === u.id}
                        aria-label={`Delete ${u.name} from Team`}
                        style={{ flex: '0 0 112px', height: 36, padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, background: '#EF444422', color: '#EF4444', border: '1px solid #EF444444', cursor: 'pointer', whiteSpace: 'nowrap', opacity: removingUser === u.id ? 0.6 : 1 }}
                      >
                        {removingUser === u.id ? 'Deleting...' : 'Delete'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
